/* eslint-disable react/style-prop-object */
/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { getFlagSvgUrl } from "components/company/v2/GeographicalCountryStack";
import { genStandardizedCountries } from "components/stocks/modern/OperatingSegments/genStandardizedCountries";
import getContourColors from "components/stocks/modern/OperatingSegments/getContourColors";
import type { FinancialSegmentsFields, UnwrappedSegment } from "components/stocks/modern/OperatingSegments/types";
import { SimpleTextTooltip, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/Tooltip";
import Twemoji from "components/ui/Twemoji";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import parseISO from "date-fns/parseISO";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import sum from "lodash/sum";
import { HelpCircleIcon } from "lucide-react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import tinycolor from "tinycolor2";

interface OperatingSegmentsListProps {
  kind: "GEOGRAPHIC" | "BUSINESS";
  forYear: number;
  financialSegments: FinancialSegmentsFields;
  segments: UnwrappedSegment[];
}
export default function OperatingSegmentsList({
  kind,
  forYear,
  financialSegments,
  segments,
}: OperatingSegmentsListProps) {
  const intl = useIntl();

  const isGeographic = kind === "GEOGRAPHIC";
  const filteredSegments = segments
    .filter((node) => node.name !== "Others")
    .filter((node) => {
      const financialIndicatorHistoryNode = (node.financialIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });
      const ratioIndicatorHistoryNode = (node.ratioIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });

      return financialIndicatorHistoryNode && ratioIndicatorHistoryNode;
    });

  const standardizedCountries = genStandardizedCountries(intl);

  const limitedSegments = filteredSegments
    .slice(0, isGeographic ? 5 : 4)
    .map((node) => {
      const standardizedCountry = standardizedCountries.find((candidate) => {
        return candidate.morningstarStandardizedName === node.name;
      });
      const financialIndicatorHistoryNode = (node.financialIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });
      const ratioIndicatorHistoryNode = (node.ratioIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });

      if (!financialIndicatorHistoryNode && !ratioIndicatorHistoryNode) {
        return null;
      }

      return {
        name: node.name,
        standardizedCountry,
        isPrimary: node.isPrimary,
        financialIndicator: financialIndicatorHistoryNode?.value || 0,
        ratioIndicator: ratioIndicatorHistoryNode?.value || 0,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => (a.ratioIndicator > b.ratioIndicator ? -1 : 1));

  const restSegments = filteredSegments.slice(5, filteredSegments.length);
  const otherSegment = segments.find((node) => node.name === "Others");

  const totalRemainingRatio =
    [...restSegments, otherSegment].filter(notUndefinedOrNull).reduce((total, node) => {
      const ratioIndicatorHistoryNode = (node.ratioIndicator?.history || []).find((candidate) => {
        return getYear(parseISO(candidate.periodEndingDate)) === forYear;
      });

      if (!ratioIndicatorHistoryNode) {
        return total;
      }

      return total + ratioIndicatorHistoryNode.value;
    }, 0) / 100;

  const hasSubTotal = limitedSegments.length < filteredSegments.length;
  const currency = financialSegments.currency === "BRL" ? "BRL" : "USD";

  return (
    <div className="h-full rounded-2xl border-none border-slate-200 bg-slate-50/80 p-4">
      <ul className="divide-y divide-gray-100">
        {limitedSegments.map(({ name, standardizedCountry, isPrimary, financialIndicator, ratioIndicator }, index) => {
          const theme = getContourColors(index);
          const description = isGeographic && standardizedCountry ? standardizedCountry.name : name;

          return (
            <li
              key={name}
              className={classNames("flex items-center justify-between gap-x-6 py-4", "first:pt-0 last:pb-0")}
            >
              <div className="space-y-1.5">
                <div className="flex items-center gap-x-2">
                  {isGeographic && standardizedCountry && (
                    <img
                      role="presentation"
                      alt={standardizedCountry.name}
                      src={getFlagSvgUrl(standardizedCountry.isoCountryCode)}
                      onContextMenu={(event) => event.preventDefault()}
                      onMouseDown={(event) => event.preventDefault()}
                      draggable={false}
                      className="h-4 w-4"
                    />
                  )}

                  <SimpleTextTooltip content={description} align="start" asChild={false}>
                    <p className="text-left text-sm font-bold text-gray-700 lg:line-clamp-1">{description}</p>
                  </SimpleTextTooltip>

                  {isPrimary && (
                    <span
                      className={classNames(
                        "rounded-xl px-1.5 text-xs font-bold tracking-wide",
                        "border border-pink-200 bg-pink-50 text-pink-600",
                      )}
                    >
                      <FormattedMessage defaultMessage="Principal" id="SRhWkB" />
                    </span>
                  )}
                </div>

                {!isGeographic && (
                  <p className="space-x-2 font-mono text-xs font-semibold text-slate-500">
                    <FormattedNumber
                      style="currency"
                      notation="compact"
                      currency={currency}
                      minimumSignificantDigits={1}
                      maximumSignificantDigits={2}
                      maximumFractionDigits={2}
                      minimumFractionDigits={2}
                      compactDisplay="short"
                      value={financialIndicator}
                    />
                  </p>
                )}
              </div>

              <div className="pl-8">
                <div className="flex items-center space-x-4">
                  <div>
                    <span className="font-mono text-xs font-semibold text-slate-700">
                      <FormattedNumber
                        style="percent"
                        maximumFractionDigits={1}
                        value={ratioIndicator ? ratioIndicator / 100 : 0}
                      />
                    </span>
                  </div>

                  <span
                    aria-hidden={true}
                    className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700"
                    style={{ backgroundColor: tinycolor(theme.background).darken(20).toHex8String() }}
                  />
                </div>
              </div>
            </li>
          );
        })}

        {(restSegments.length > 0 || (otherSegment && totalRemainingRatio > 0)) && (
          <li className={classNames("space-y-2 py-4", "first:pt-0 last:pb-0")}>
            <div className={classNames("flex items-center justify-between gap-x-6")}>
              <div className="flex items-center space-x-2">
                {isGeographic ? (
                  <>
                    <Twemoji emoji="🌎" width={16} height={16} />

                    <div className="line-clamp-2 text-sm font-bold text-gray-700">
                      <SimpleTextTooltip
                        align="start"
                        content={intl.formatMessage({
                          defaultMessage: "Países não especificados pela empresa ou não padronizados pela Morningstar.",
                          id: "ZFwD5O",
                          description: "Tooltip do item 'Outros' do gráfico de receitas",
                        })}
                      >
                        <span>
                          <FormattedMessage
                            defaultMessage="Outros"
                            description="Complemento do gráfico de receitas"
                            id="8K4Qok"
                          />
                        </span>
                      </SimpleTextTooltip>
                    </div>
                  </>
                ) : (
                  <div className="line-clamp-2 text-sm font-bold text-gray-700">
                    <SimpleTextTooltip
                      align="start"
                      content={intl.formatMessage({
                        defaultMessage: "Receitas não especificadas pela empresa ou não padronizadas pela Morningstar.",
                        id: "50tDdK",
                        description: "Tooltip do item 'Demais Receitas' do gráfico de receitas",
                      })}
                    >
                      <span>
                        <FormattedMessage
                          defaultMessage="Demais Receitas"
                          description="Complemento do gráfico de receitas"
                          id="K+eiwc"
                        />
                      </span>
                    </SimpleTextTooltip>
                  </div>
                )}
              </div>

              <div className="pl-8">
                <div className="flex items-center space-x-4">
                  <div>
                    <span className="font-mono text-xs font-semibold text-slate-700">
                      <FormattedNumber style="percent" maximumFractionDigits={1} value={totalRemainingRatio} />
                    </span>
                  </div>

                  <span
                    aria-hidden={true}
                    className="relative inline-flex h-2.5 w-2.5 items-center justify-center rounded-full bg-violet-700"
                    style={{
                      backgroundColor: tinycolor(getContourColors(limitedSegments.length + 1).background)
                        .darken(20)
                        .toHex8String(),
                    }}
                  />
                </div>
              </div>
            </div>
          </li>
        )}

        {!isGeographic && (
          <li className={classNames("space-y-2 py-4", "first:pt-0 last:pb-0")}>
            {hasSubTotal && (
              <div className={classNames("flex items-center justify-between gap-x-6")}>
                <div className="space-y-1.5">
                  <p className="line-clamp-2 text-sm font-bold text-gray-700">Subtotal ({limitedSegments.length})</p>
                </div>

                <div className="pl-8">
                  <p className="font-mono text-sm font-bold text-slate-600">
                    <FormattedNumber
                      style="currency"
                      notation="compact"
                      currency={currency}
                      minimumSignificantDigits={1}
                      maximumSignificantDigits={4}
                      maximumFractionDigits={2}
                      minimumFractionDigits={2}
                      compactDisplay="short"
                      value={sum(limitedSegments.map((node) => node.financialIndicator))}
                    />
                  </p>
                </div>
              </div>
            )}

            <div className={classNames("flex items-center justify-between gap-x-6")}>
              <div className="flex items-center space-x-1.5">
                <p className="line-clamp-2 text-sm font-bold text-gray-700">
                  {hasSubTotal ? `Total (${filteredSegments.length})` : "Total"}
                </p>

                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircleIcon className="h-4 w-4" />
                    </TooltipTrigger>

                    <TooltipContent className="w-80 rounded-lg bg-gray-600 p-2 font-aeonik text-xs" side="top">
                      <span className="font-bold tracking-wider text-white">
                        <FormattedMessage
                          defaultMessage="O cálculo dos % não considera valores negativos na Receita como eliminações ou descontos, por isso o valor total acima pode ser diferente da tabela fundamentalista, além de pequenos arredondamentos nos valores."
                          id="HKM/8P"
                          description="Conteúdo do tooltip de ajuda da Receita por negócio"
                        />
                      </span>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="pl-8">
                <p className="font-mono text-sm font-bold text-slate-600">
                  <FormattedNumber
                    style="currency"
                    notation="compact"
                    currency={currency}
                    minimumSignificantDigits={1}
                    maximumSignificantDigits={4}
                    maximumFractionDigits={2}
                    minimumFractionDigits={2}
                    compactDisplay="short"
                    value={sum(
                      segments.map((node) => {
                        const financialIndicatorHistory = (node.financialIndicator?.history || []).find((candidate) => {
                          return getYear(parseISO(candidate.periodEndingDate)) === forYear;
                        });

                        return financialIndicatorHistory?.value || 0;
                      }),
                    )}
                  />
                </p>
              </div>
            </div>
          </li>
        )}
      </ul>

      {financialSegments.periodEndingDate && false && (
        <p className="pt-2 text-xs font-medium text-gray-500">
          <FormattedMessage
            defaultMessage="Relatório de {year}"
            id="BoGyKM"
            values={{
              year: getYear(parseISO(financialSegments.periodEndingDate)),
            }}
          />
        </p>
      )}
    </div>
  );
}
